import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Snackbar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { Mail, Link as LinkIcon, RefreshCw, Star, Trash2, Plus, Check } from 'lucide-react';
import axios from 'axios';
import * as constants from '../../../../utils/constants';
import { EmailInbox } from '../../../Email';

const API_URL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}`;

/**
 * OutlookEmailPage - Outlook email integration page for HrDashboard
 * Supports multiple Outlook accounts with default selection
 */
const OutlookEmailPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [settingDefault, setSettingDefault] = useState(null);

  // Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('authToken');
    return {
      Authorization: `Token ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  // Check connection status
  const checkConnectionStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/outlook/auth/status/`,
        { headers: getAuthHeaders() }
      );
      setConnectionStatus(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setConnectionStatus({ connected: false, connections: [] });
      } else {
        setError(err.response?.data?.error || 'Failed to check connection status');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Initiate OAuth connection (add another account)
  const initiateConnection = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/outlook/auth/initiate/`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate connection');
      setConnecting(false);
    }
  }, [getAuthHeaders]);

  // Disconnect a specific account
  const disconnectAccount = useCallback(async (connectionId) => {
    setDisconnecting(connectionId);
    try {
      await axios.delete(
        `${API_URL}/api/v1/outlook/auth/disconnect/?connection_id=${connectionId}`,
        { headers: getAuthHeaders() }
      );
      setSuccessMessage('Account disconnected successfully');
      await checkConnectionStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disconnect');
    } finally {
      setDisconnecting(null);
    }
  }, [getAuthHeaders, checkConnectionStatus]);

  // Set an account as default
  const setDefault = useCallback(async (connectionId) => {
    setSettingDefault(connectionId);
    try {
      await axios.post(
        `${API_URL}/api/v1/outlook/auth/set-default/`,
        { connection_id: connectionId },
        { headers: getAuthHeaders() }
      );
      setSuccessMessage('Default account updated');
      await checkConnectionStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set default');
    } finally {
      setSettingDefault(null);
    }
  }, [getAuthHeaders, checkConnectionStatus]);

  // Handle OAuth redirect parameters
  useEffect(() => {
    const connected = searchParams.get('connected');
    const oauthError = searchParams.get('error');
    const email = searchParams.get('email');

    if (connected === 'true') {
      setSuccessMessage(`Successfully connected ${email || 'Outlook account'}!`);
      setSearchParams({});
    } else if (oauthError) {
      const errorMessages = {
        'oauth_failed': 'Failed to connect Outlook account. Please try again.',
        'missing_code': 'Authorization failed. Please try again.',
        'state_expired': 'Session expired. Please try again.',
        'invalid_state': 'Invalid session. Please try again.',
        'access_denied': 'Access was denied. Please grant permissions to connect.',
      };
      setError(errorMessages[oauthError] || `Connection error: ${oauthError}`);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Check status on mount
  useEffect(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  const connections = connectionStatus?.connections || [];
  const defaultConnection = connectionStatus?.default_connection;

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 200px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not connected state - no accounts at all
  if (!connectionStatus?.connected) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 200px)',
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 400, textAlign: 'center' }}>
          <CardContent>
            <Mail size={48} style={{ color: '#0078D4', marginBottom: 16 }} />
            <Typography variant="h5" gutterBottom>
              Connect Microsoft Outlook
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect your Microsoft Outlook account to view and manage your emails directly from CRM.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </CardContent>
          <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={connecting ? <CircularProgress size={20} /> : <LinkIcon size={20} />}
              onClick={initiateConnection}
              disabled={connecting}
              sx={{ 
                bgcolor: '#0078D4',
                '&:hover': { bgcolor: '#106EBE' },
              }}
            >
              {connecting ? 'Connecting...' : 'Connect Outlook'}
            </Button>
          </CardActions>
        </Card>
      </Box>
    );
  }

  // Connected - show accounts management + inbox
  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="h5">Outlook Email</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshCw size={16} />}
            onClick={checkConnectionStatus}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Connected Accounts Bar */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          bgcolor: '#fafafa',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#555', mr: 0.5 }}>
          Accounts:
        </Typography>
        {connections.map((conn) => (
          <Box
            key={conn.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              border: conn.is_default ? '1.5px solid #0078D4' : '1px solid #ddd',
              borderRadius: '20px',
              px: 1.5,
              py: 0.5,
              bgcolor: conn.is_default ? '#e8f0fe' : '#fff',
            }}
          >
            {conn.is_default && (
              <Star size={14} fill="#0078D4" color="#0078D4" />
            )}
            <Typography variant="body2" sx={{ fontWeight: conn.is_default ? 600 : 400, fontSize: '0.8rem' }}>
              {conn.email}
            </Typography>
            {conn.is_default && (
              <Chip label="Default" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#0078D4', color: '#fff' }} />
            )}
            {!conn.is_default && (
              <Tooltip title="Set as default">
                <IconButton
                  size="small"
                  onClick={() => setDefault(conn.id)}
                  disabled={settingDefault === conn.id}
                  sx={{ p: 0.25 }}
                >
                  {settingDefault === conn.id ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Check size={14} color="#666" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Disconnect this account">
              <IconButton
                size="small"
                onClick={() => disconnectAccount(conn.id)}
                disabled={disconnecting === conn.id}
                sx={{ p: 0.25 }}
              >
                {disconnecting === conn.id ? (
                  <CircularProgress size={14} />
                ) : (
                  <Trash2 size={14} color="#d32f2f" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        ))}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Button
          size="small"
          startIcon={connecting ? <CircularProgress size={14} /> : <Plus size={14} />}
          onClick={initiateConnection}
          disabled={connecting}
          sx={{ fontSize: '0.75rem', textTransform: 'none' }}
        >
          {connecting ? 'Connecting...' : 'Add Account'}
        </Button>
      </Paper>

      {/* Email Inbox - uses default connection */}
      <Paper sx={{ flex: 1, overflow: 'hidden' }}>
        <EmailInbox
          connectionId={defaultConnection?.id}
          height="100%"
          showSidebar={true}
        />
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default OutlookEmailPage;
